import { useMutation } from '@tanstack/react-query';
import { collectBrowserInfo, handleRedirect } from '@is24/is24-checkout-js';
import { useEffect } from 'react';
import { Product } from '@/@types/upsell';
import { ProductType } from '@/@types/product';
import { upsellPurchase } from '@/api/upsellPurchase/upsellPurchase';
import { usePollUpsellPurchaseResult } from '@/apiHooks/usePollUpsellPurchaseResult.hook';
import { useRefetchUntilProduct } from '@/apiHooks/useRefetchUntilProduct.hook';

/**
 * One-click upsell purchase: mutation → transaction polling → product confirmation.
 * Resolves via immediate grant, 3DS redirect, or async transaction polling.
 * {@link onSuccess} fires once the product appears in active products.
 */
export const useUpsellPurchase = (onSuccess: () => void) => {
  const { startPolling, isPolling, isConfirmed } = useRefetchUntilProduct(onSuccess);

  const {
    isError: isPurchaseError,
    isLoading,
    data: upsellPurchaseResponse,
    mutate,
    reset,
    variables,
  } = useMutation({
    mutationFn: ({
      product,
      googlePlayExternalTransactionToken,
      trackingInfo,
    }: {
      product: Product;
      googlePlayExternalTransactionToken?: string;
      trackingInfo: string;
    }) => upsellPurchase(product, collectBrowserInfo(), googlePlayExternalTransactionToken, trackingInfo),
    onSuccess: (response) => {
      if (response?.productGranted || response?.alreadyEntitled) return startPolling(ProductType.WohnenPlus);
      if (response?.inProgress && response.checkoutRedirectData) handleRedirect(response.checkoutRedirectData);
    },
  });

  const shouldPollTransaction =
    upsellPurchaseResponse?.inProgress === true && !upsellPurchaseResponse?.checkoutRedirectData;
  const { isGranted } = usePollUpsellPurchaseResult(
    upsellPurchaseResponse?.transactionId ?? '',
    shouldPollTransaction,
    variables?.trackingInfo,
    variables?.googlePlayExternalTransactionToken,
  );

  useEffect(() => {
    if (isGranted) startPolling(ProductType.WohnenPlus);
  }, [isGranted, startPolling]);

  const isUnexpectedResponse =
    !!upsellPurchaseResponse &&
    !upsellPurchaseResponse.productGranted &&
    !upsellPurchaseResponse.alreadyEntitled &&
    !upsellPurchaseResponse.inProgress;

  return {
    isSuccess: isConfirmed,
    isError: isPurchaseError || isUnexpectedResponse,
    isLoading: isLoading || shouldPollTransaction || isPolling,
    resetError: reset,
    upsellPurchase: (product: Product, googlePlayExternalTransactionToken?: string) =>
      mutate({
        product,
        googlePlayExternalTransactionToken,
        trackingInfo: btoa(new URLSearchParams(window.location.search).toString()),
      }),
  };
};
